import React from 'react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useGetStats } from './queries/statsQueries';
import { SitesResponse, SportsResponse, SlateResponse } from '../common/store/types';
import DataGrid from '../common/Table/newTable';
import { getMmaTopStacksColumns } from './stats/statsTableConfig';

interface OwnProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedSlate?: SlateResponse;
  readonly tableHeight?: string;
  readonly backgroundColor?: string;
  readonly emptyText?: string;
}

const nflColumns: GridColDef[] = [
  { field: 'team', headerName: 'Team', width: 100, flex: 1 },
  { field: 'opp', headerName: 'Opp', width: 100, flex: 1 },
  {
    field: 'qbOwn',
    headerName: 'QB Own %',
    width: 120,
    flex: 1,
    type: 'number',
    valueGetter: (params) => params.row.qbOwn != null ? params.row.qbOwn / 100 : null,
    renderCell: (params) => params.row.qbOwn != null ? `${params.row.qbOwn.toFixed(1)}%` : '-'
  },
  {
    field: 'topStack',
    headerName: 'Top Stack %',
    width: 120,
    flex: 1,
    type: 'number',
    valueGetter: (params) => params.row.topStack != null ? params.row.topStack / 100 : null,
    renderCell: (params) => params.row.topStack != null ? `${params.row.topStack.toFixed(2)}%` : '-'
  },
  {
    field: 'avgSal',
    headerName: 'Avg Sal',
    width: 100,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : '-'
  },
  {
    field: 'topValue',
    headerName: 'Top Value',
    width: 100,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
  {
    field: 'rushPercent',
    headerName: 'Rush %',
    width: 100,
    flex: 1,
    type: 'number',
    valueGetter: (params) => params.row.rushPercent != null ? params.row.rushPercent / 100 : null,
    renderCell: (params) => params.row.rushPercent != null ? `${params.row.rushPercent.toFixed(1)}%` : '-'
  },
  {
    field: 'rating',
    headerName: 'Rating',
    width: 80,
    flex: 1,
    type: 'number'
  },
];

const nhlColumns: GridColDef[] = [
  { field: 'team', headerName: 'Team', width: 100, flex: 1 },
  { field: 'line', headerName: 'Line', width: 80, flex: 1, type: 'number' },
  { field: 'p1', headerName: 'P1', width: 120, flex: 1 },
  { field: 'p2', headerName: 'P2', width: 120, flex: 1 },
  { field: 'p3', headerName: 'P3', width: 120, flex: 1 },
  {
    field: 'lineSalary',
    headerName: 'Line Salary',
    width: 120,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : '-'
  },
  {
    field: 'top2StackPercent',
    headerName: 'Top 2 Stack %',
    width: 130,
    flex: 1,
    type: 'number',
    valueGetter: (params) => params.row.top2StackPercent != null ? params.row.top2StackPercent / 100 : null,
    renderCell: (params) => params.row.top2StackPercent != null ? `${params.row.top2StackPercent.toFixed(2)}%` : '-'
  },
  {
    field: 'ownershipShare',
    headerName: 'Own Share',
    width: 120,
    flex: 1,
    type: 'number',
    valueGetter: (params) => params.row.ownershipShare != null ? params.row.ownershipShare / 100 : null,
    renderCell: (params) => params.row.ownershipShare != null ? `${params.row.ownershipShare.toFixed(2)}%` : '-'
  },
  {
    field: 'leverage',
    headerName: 'Leverage',
    width: 100,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
];

const mlbColumns: GridColDef[] = [
  { field: 'team', headerName: 'Team', width: 100, flex: 1 },
  { field: 'matchup', headerName: 'Matchup', width: 120, flex: 1 },
  { field: 'oppSp', headerName: 'Opp. SP', width: 120, flex: 1 },
  {
    field: 'oppSpOwn',
    headerName: 'Opp. SP Own',
    width: 120,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '-'
  },
  {
    field: 'teamTotal',
    headerName: 'Team Total',
    width: 110,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
  {
    field: 'topStackPercent',
    headerName: 'Top Stack %',
    width: 120,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '-'
  },
  {
    field: 'topValuePercent',
    headerName: 'Top Value %',
    width: 120,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '-'
  },
  {
    field: 'ownershipShare',
    headerName: 'Own Share',
    width: 110,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? `${params.value.toFixed(1)}%` : '-'
  },
  {
    field: 'leverage',
    headerName: 'Leverage',
    width: 100,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
  {
    field: 'stackScore',
    headerName: 'Stack Score',
    width: 110,
    flex: 1,
    type: 'number',
    renderCell: (params) => params.value != null ? params.value.toFixed(2) : '-'
  },
];

// Convert columns for mobile view - remove flex and use fixed widths
const getMobileColumns = (columns: GridColDef[]): GridColDef[] => {
  return columns.map(col => ({
    ...col,
    flex: undefined,
    width: col.width || 80,
    minWidth: col.width || 80,
  }));
};

const TopStacks = (props: OwnProps) => {
  const { selectedSport, selectedSite, selectedSlate, tableHeight, backgroundColor, emptyText } = props;

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isMLB = selectedSport?.sport === 'MLB';
  const isDisabled = !selectedSport?.sport || !selectedSite?.site || (isMLB && !selectedSlate?.slateId);
  const { data: topStacks, isLoading } = useGetStats(
    selectedSport?.sport || '',
    'topstacks',
    !isDisabled,
    selectedSite?.site || 'DK',
    isMLB ? selectedSlate?.slateId : undefined
  );

  const isNHL = selectedSport?.sport === 'NHL';
  const isMMA = selectedSport?.sport === 'MMA';
  const baseColumns = isMMA ? getMmaTopStacksColumns() : isMLB ? mlbColumns : isNHL ? nhlColumns : nflColumns;

  // Use mobile-optimized columns on mobile devices
  const columns = isMobile ? getMobileColumns(baseColumns) : baseColumns;

  // Determine which column to pin on mobile
  const pinnedColumn = isMMA ? 'name' : 'team';

  // Add unique row ID based on data type
  const getRowId = (row: any) => {
    if (isMMA) return `${row.name}-${row.fight}`;
    if (isNHL) return `${row.team}-${row.line}`;
    if (isMLB) return `${row.team}-${row.matchup}`;
    return `${row.team}-${row.opp}`;
  };

  // Transform data to include id field for DataGrid
  const rows = React.useMemo(() => {
    return (topStacks || []).map((row: any, index: number) => ({
      ...row,
      id: index + 1,
      nameAndId: getRowId(row), // For DataGrid row identification
    }));
  }, [topStacks, isMMA, isNHL, isMLB]);

  return (
    <Box height={tableHeight || '100%'}>
      <DataGrid
        lineups={rows}
        headers={columns}
        isLoading={isLoading}
        selectedView="topstacks"
        simType="dataHub"
        includeStacks={false}
        setStackView={() => {}}
        tierlocked={false}
        setPlayerList={() => {}}
        setStackList={() => {}}
        pagination
        rowHeight={isMobile ? 52 : 44}
        fontSize={isMobile ? 12 : 14}
        emptyText={emptyText || 'Select a sport and site to display top stacks.'}
        backgroundColor={backgroundColor || 'rgba(125, 125, 125, .25)'}
        isMobile={isMobile}
        pinnedColumnsLeft={isMobile ? [pinnedColumn] : undefined}
      />
    </Box>
  );
};

export default TopStacks;
